import { useEffect, useRef } from "react";

type Options = {
  open: boolean;
  enabled: boolean;
};

const DISPLAY_NONE = "none";
const VISIBILITY_HIDDEN = "hidden";
const FOCUSABLE_SELECTOR = [
  "[autofocus]:not([disabled])",
  'input:not([type="hidden"]):not([disabled]):not([readonly])',
  "textarea:not([disabled]):not([readonly])",
  "select:not([disabled])",
  '[contenteditable="true"]',
  ".ck-select-selection-search-input:not([disabled])",
  ".ck-picker-input:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute("hidden")) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  if (style.display === DISPLAY_NONE || style.visibility === VISIBILITY_HIDDEN) return false;
  return true;
}

function findFirstFocusable(root: HTMLElement): HTMLElement | null {
  // `data-autofocus` wählt das Startfeld ausdrücklich: React schreibt `autoFocus` nicht als Attribut ins DOM.
  const explicit = root.querySelector<HTMLElement>("[autofocus]:not([disabled]), [data-autofocus]:not([disabled])");
  if (explicit && isVisible(explicit)) return explicit;
  const candidates = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  for (const el of Array.from(candidates)) {
    if (isVisible(el)) return el;
  }
  return null;
}

/**
 * Setzt nach dem Öffnen eines Modal/Drawer den Fokus auf das erste interaktive Feld.
 *
 * Antd mountet den Body-Inhalt erst nach dem Open-Event, daher doppeltes
 * `requestAnimationFrame`, damit Inputs und Antd-DOM-Wrapper im Layout sind.
 * Produktneutral — keine projektspezifischen Abhängigkeiten.
 */
export function useAutoFocusInContainer({ open, enabled }: Options) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !enabled) return;
    let raf = 0;
    let cancelled = false;
    const schedule = () => {
      raf = window.requestAnimationFrame(() => {
        if (cancelled) return;
        raf = window.requestAnimationFrame(() => {
          if (cancelled) return;
          const root = ref.current;
          if (!root) return;
          const target = findFirstFocusable(root);
          target?.focus();
        });
      });
    };
    schedule();
    return () => {
      cancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [open, enabled]);

  return ref;
}
